import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { isFieldForListingType } from '../../util/fieldHelpers';
import { fitFactRows } from '../../util/answerContentCore';
import { brandDisplayName } from '../../util/brandLabel';
import { boltCircleText } from '../../util/boltCircleCore';

import { Heading } from '../../components';
import css from './ListingPage.module.css';

/* Lucide stroke-1.6 spec icons — one per known field key. Every row
 * gets an icon; keys not explicitly mapped fall back to a neutral
 * "info" dot so the layout stays consistent (icons aren't optional,
 * they're a scan anchor — missing some but not others reads as
 * inconsistent craft). Stroke scales via currentColor. */
const ICON_BY_KEY = {
  // Car / fitment
  brand: 'car',
  carBrand: 'car',
  carModel: 'car',
  carName: 'car',
  categoryDisplayText: 'car',
  bilmodell: 'car',
  // Dimensions
  combination: 'circle-tire',
  diameter: 'circle-tire',
  tireWidthFront: 'circle-tire',
  tireWidthRear: 'circle-tire',
  profileFront: 'circle-tire',
  profileRear: 'circle-tire',
  rimWidthFront: 'circle-tire',
  rimWidthRear: 'circle-tire',
  // Rim / wheel attachment
  boltPattern: 'wrench',
  numberOfBolts: 'wrench',
  boltCircle: 'wrench',
  hubHole: 'wrench',
  etFront: 'wrench',
  etRear: 'wrench',
  rimType: 'wrench',
  // Tire properties
  threaddepth: 'gauge',
  patternDepth: 'gauge',
  sasong: 'snowflake',
  season: 'snowflake',
  tyreType: 'snowflake',
  wheelType: 'snowflake',
  // Condition / product
  condition: 'check-circle',
  product: 'package',
  quantity: 'hash',
  // Location / logistics
  warehouse: 'pin',
  location: 'pin',
  city: 'pin',
  county: 'pin',
  // Admin / meta
  reference: 'tag',
  listingNumber: 'tag',
  referens: 'tag',

  /* Aliases keyed on Swedish heading text — sectionTextProps uses
   * heading strings rather than enum keys, so the resolver
   * lowercases + de-accents the heading to find a match here. */
  bilmärke: 'car',
  bilmarke: 'car',
  däckdimension: 'circle-tire',
  dackdimension: 'circle-tire',
  dimension: 'circle-tire',
  produkt: 'package',
  däcktyp: 'snowflake',
  dacktyp: 'snowflake',
  säsong: 'snowflake',
  skick: 'check-circle',
  mönsterdjup: 'gauge',
  monsterdjup: 'gauge',
  bultcirkel: 'wrench',
  'bultmönster': 'wrench',
  bultmonster: 'wrench',
  navhål: 'wrench',
  navhal: 'wrench',
  antal: 'hash',
  plats: 'pin',
  lager: 'package',
};

const Icons = {
  car: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17h10M5 11l1.5-4a2 2 0 012-1.5h7a2 2 0 012 1.5L19 11M5 11h14v5a1 1 0 01-1 1h-2v-2H8v2H6a1 1 0 01-1-1v-5z" />
      <circle cx="8" cy="14.5" r="1" />
      <circle cx="16" cy="14.5" r="1" />
    </svg>
  ),
  'circle-tire': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  ),
  wrench: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a4 4 0 00-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 005.4-5.4l-2.5 2.5-2.9-2.9z" />
    </svg>
  ),
  gauge: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  ),
  snowflake: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="2" x2="12" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="5" y1="5" x2="19" y2="19" />
      <line x1="5" y1="19" x2="19" y2="5" />
    </svg>
  ),
  'check-circle': (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  package: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  hash: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  ),
  tag: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  ),
  pin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  /* Fallback — Lucide `info` dot. Used whenever a row's key isn't in
   * ICON_BY_KEY so the column stays visually uniform. */
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};

const normKey = raw =>
  String(raw || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const DetailIcon = ({ iconKey }) => {
  const name = ICON_BY_KEY[iconKey] || ICON_BY_KEY[normKey(iconKey)] || 'info';
  return <span className={css.detailIcon}>{Icons[name]}</span>;
};

const SectionDetailsMaybe = props => {
  const { publicData, metadata = {}, listingFieldConfigs, isFieldForCategory, intl, sectionTextProps,
    isForGeneration } = props;

  if (!publicData || !listingFieldConfigs) {
    return null;
  }
  // --- DISPLAY TEXT (moved from SectionCategoryMaybe) ---
  const { categoryLevel1, categoryLevel2, carName } = publicData || {};
  const selectedCategory = props.categoryConfiguration?.categories?.find(
    c => c.id == categoryLevel1
  ) || {};
  const selectedSubCategory =
    selectedCategory?.subcategories?.find(sc => sc.id == categoryLevel2) || {};

  const categoryDisplay = selectedCategory?.name || "";
  const subcategoryDisplay = selectedSubCategory?.name || "";

  const displayText = carName
    ? carName
    : `${categoryDisplay} ${subcategoryDisplay}`.trim();


  const pickListingFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue || metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const getBooleanMessage = value =>
        value
          ? intl.formatMessage({ id: 'SearchPage.detailYes' })
          : intl.formatMessage({ id: 'SearchPage.detailNo' });
      const optionConfig = findSelectedOption(value);

      /* P25009-482 BR-11 / AC-6 — the brand is printed as a name ("GT Radial"),
       * never as its key, including keys the hosted enum does not list yet.
       * A placeholder ("other") names no manufacturer and gets no row. */
      if (key === 'brand') {
        const brandName = brandDisplayName(value, listingFieldConfigs);
        return brandName ? filteredConfigs.concat({ key, value: brandName, label }) : filteredConfigs;
      }

      return schemaType === 'enum'
        ? filteredConfigs.concat({ key, value: optionConfig?.label, label })
        : schemaType === 'boolean'
          ? filteredConfigs.concat({ key, value: getBooleanMessage(value), label })
          : schemaType === 'long'
            ? filteredConfigs.concat({ key, value, label })
            : filteredConfigs;
    }
    return filteredConfigs;
  };

  let existingListingFields = listingFieldConfigs.reduce(pickListingFields, []);


  const numberOfBolts = publicData?.numberOfBolts;
  const boltCircle = publicData?.boltCircle;

  const summaryLabel = intl.formatMessage({ id: 'ListingPage.combinationLabel' });

  const combinationArray = [];
  const { tireWidthFront, profileFront, tireWidthRear, profileRear, diameter } = publicData;

  // Construct front combination string if data exists
  if (tireWidthFront && profileFront && diameter) {
    combinationArray.push(`${tireWidthFront}/${profileFront}R${diameter}`);
  }

  // Construct rear combination string if data exists and it's different from front
  if (tireWidthRear && profileRear && diameter) {
    const rearCombination = `${tireWidthRear}/${profileRear}R${diameter}`;
    if (!combinationArray.includes(rearCombination)) {
      combinationArray.push(rearCombination);
    }
  }




  const keysToExclude = [];

  if (numberOfBolts && boltCircle) {
    /* The circle as the number it means: the stored value is Console's key,
     * "114_3" or "1397" for 139.7 (P25009-488). */
    const combinedValue = `${numberOfBolts}/${boltCircleText(boltCircle) || boltCircle}`;
    existingListingFields = existingListingFields.filter(
      field => field.key !== 'numberOfBolts' && field.key !== 'boltCircle'
    );
    existingListingFields.unshift({
      key: 'boltPattern',
      label: 'Bultcirkel',
      value: combinedValue,
    });
    keysToExclude.push('numberOfBolts', 'boltCircle');
  }

  if (combinationArray.length > 0) {
    /* The size rows ARE these fields, so these fields — and only these — are
     * not repeated as rows of their own. This used to drop ANY field whose
     * value appeared somewhere inside a size string, so a rear rim width of 9
     * vanished because "R19" contains a 9, and an ET of 55 because
     * "235/55R19" contains 55 (P25009-480 BR-8). */
    const frontShown = tireWidthFront && profileFront && diameter;
    const rearShown = tireWidthRear && profileRear && diameter;
    const valuesToRemove = new Set([
      ...(frontShown ? ['tireWidthFront', 'profileFront'] : []),
      ...(rearShown ? ['tireWidthRear', 'profileRear'] : []),
      'diameter',
    ]);

    existingListingFields = existingListingFields.filter(field => !valuesToRemove.has(field.key));

    existingListingFields.unshift({
      key: 'combination',
      label: summaryLabel,
      value: combinationArray.map((c, i) => (
        <div key={`combination-${i}`}>{c}</div>
      )),
    });
    if (displayText) {
      existingListingFields.unshift({
        key: 'categoryDisplayText',
        label: intl.formatMessage({ id: "SectionCategoryMaybe.title" }),
        value: displayText,
      });
    }


    keysToExclude.push(...valuesToRemove);
    keysToExclude.push('combination');
  }



  const selection = [
    'tireWidthFront',
    'tireWidthRear',
    'profileFront',
    'profileRear',
    'diameter',
    'rimType',
    'patternDepth',
    'numberOfBolts',
    'boltCircle',
    'rimWidthFront',
    'rimWidthRear',
    'hubHole',
    'etFront',
    'etRear',
    'carBrand',
  ];

  /* P25009-409 BR-3 lists the facts a buyer (and an AI answer) needs, and two of
   * them never reach the rows above: the tyre/rim model, which the supplier feed
   * writes but the Console has no listing field for, and the TPMS answer, which
   * the create-ad form stores as a plain boolean. Everything else on BR-3's list
   * is already a configured listing field, so only the genuinely missing rows
   * are appended, and a Console change that adds one of them later silently
   * wins instead of producing the row twice.
   *
   * Duplicates are ruled out two ways, because a fact can arrive by two
   * different routes:
   *   by key    for the enum, boolean and long fields, which come through
   *             pickListingFields above with their Console key intact.
   *   by label  for the TEXT fields, which come through `sectionTextProps`
   *             keyed on their Swedish heading rather than on a key —
   *             "Mönsterdjup" is one, and matching on key alone showed it twice.
   *
   * `carFitment` needs naming explicitly: the same fact is shown under
   * `categoryDisplayText`, and the two labels have nothing in common. */
  const CORE_ROW_ALIASES = { carFitment: 'categoryDisplayText' };
  const shownKeys = new Set([
    ...existingListingFields.map(field => field.key),
    ...keysToExclude,
  ]);
  const shownLabels = new Set(
    [
      ...existingListingFields.map(field => field.label),
      ...(sectionTextProps || []).map(prop => prop.heading),
    ]
      .filter(Boolean)
      .map(normKey)
  );
  const extraFitRows = fitFactRows(publicData, {
    labelFor: (key, value) => {
      const fieldConfig = listingFieldConfigs.find(f => f.key === key);
      return fieldConfig?.enumOptions?.find(o => `${o.option}` === `${value}`)?.label || value;
    },
  }).filter(
    row =>
      !shownKeys.has(row.key) &&
      !shownKeys.has(CORE_ROW_ALIASES[row.key]) &&
      !shownLabels.has(normKey(row.label))
  );

  const allRows = [...existingListingFields, ...extraFitRows];
  const textRows = Array.isArray(sectionTextProps) ? sectionTextProps : [];

  /* P25009-512 BR-1 — the listing number ("Annonsnr"), for every visitor, as the
   * table's last row in small grey text: customers read it out when they call.
   * Built here rather than from fitFactRows, so it never reaches the structured
   * data or the AI answer pages built from those rows (C-17), and left off the
   * fixed-size share image. No number, no row (BR-4). */
  const listingNumber =
    typeof metadata?.tireId === 'string' && metadata.tireId.trim() ? metadata.tireId.trim() : null;
  const showListingNumber = !!listingNumber && !isForGeneration;

  /* The guard counts the text rows too. It used to look at the enum/long fields
   * alone, so a listing whose only facts were TEXT fields — a bulk-imported
   * tyre with a tread depth and nothing else — rendered no table at all while
   * holding a fact the table was built to show. */
  return allRows.length > 0 || textRows.length > 0 || showListingNumber ? (
    <div
      style={
        isForGeneration
          ? {
            transform: "scale(0.55)",
            transformOrigin: "top left",
            width: "182%", // keeps layout correct after shrinking
          }
          : {}
      }
    >
      <section className={css.sectionDetails}>
        <div className={css.detailsContainer}>
          <h2 className={css.InfoTitle}>
            <FormattedMessage id="ListingPage.detailsTitle" />
          </h2>
          {/* P25009-409 BR-3 / AC-2 — a real <table>, not a grid of divs. These
            * are the facts an AI answer engine quotes when it says which wheel
            * fits which car, and it reads them straight out of the markup: a
            * label and its value only read as a pair inside a table row.
            *
            * The card layout is unchanged — the CSS lays the table out as a
            * grid. That is exactly why the ARIA roles are written out by hand:
            * any `display` other than `table` drops the implicit roles in most
            * browsers, so a screen reader would lose the pairing that the
            * crawler can still see in the tags. */}
          <table className={css.detailsRowContainer} role="table">
            <tbody className={css.detailsTableBody} role="rowgroup">
            {allRows.map(detail => (
              <tr
                className={css.detailsRow}
                key={detail.key}
                role="row"
                title={
                  selection.includes(detail.key)
                    ? intl.formatMessage({ id: `EditListingDetailsForm.tooltip.${detail.key}` })
                    : undefined
                }
              >
                <th className={css.detailHeader} scope="row" role="rowheader">
                  <DetailIcon iconKey={detail.key} />
                  <span className={css.detailLabel}>{detail.label}</span>
                </th>
                <td className={css.detailValue} role="cell">
                  {detail.value}
                </td>
              </tr>
            ))}
            {/* sectionTextProps — ad-hoc rows (Mönsterdjup, Referens)
              * coming from outside the listingField enum system.
              * Uses the same DetailIcon + detailHeader shape so these
              * rows look identical to the rest of the grid. Icon key
              * is derived from the heading text by lowercasing +
              * stripping diacritics; unmapped headings fall back to
              * the neutral info dot. */}
            {sectionTextProps && Array.isArray(sectionTextProps) && sectionTextProps.length > 0 &&
              sectionTextProps.map((textProp, index) => {
                const iconKey = (textProp.iconKey
                  || String(textProp.heading || '')
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, ''))
                  .trim();
                return (
                  <tr className={css.detailsRow} key={`sectionText-${index}`} role="row">
                    <th className={css.detailHeader} scope="row" role="rowheader">
                      <DetailIcon iconKey={iconKey} />
                      <span className={css.detailLabel}>{textProp.heading}</span>
                    </th>
                    <td className={css.detailValue} role="cell">
                      {textProp.text}
                    </td>
                  </tr>
                );
              })}
            {showListingNumber ? (
              <tr className={css.detailsRow} key="listingNumber" role="row">
                <th className={css.detailHeader} scope="row" role="rowheader">
                  <DetailIcon iconKey="listingNumber" />
                  <span className={css.detailLabel}>
                    <FormattedMessage id="ListingPage.listingNumber" />
                  </span>
                </th>
                <td className={`${css.detailValue} ${css.detailValueMuted}`} role="cell">
                  {listingNumber}
                </td>
              </tr>
            ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  ) : null;
};


export default SectionDetailsMaybe;
